/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form } from 'antd';
import { SimulateUseCaseInterface } from 'application/usecases/SimulateUseCase';
import { SimHistoryInfo, SimulateEntity } from 'domain/entities/SimulateEntity';
import { SCREEN_NAME } from 'infrastructure/config/CONSTANTS';
import { openSuccessNoti } from 'presentation/components/Notification';
import { PageTitle } from 'presentation/components/PageTitle';
import {
    completeFetching,
    showConfirm,
    startFetching,
} from 'presentation/redux/features/app/appSlice';
import { SimulateSaleProperty } from 'domain/entities/PropertyEntity';
import { useAppSelector } from 'presentation/redux/hooks';
import { AUTH_ROUTES } from 'presentation/router/CONSTANTS';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import Content from './components/Content';
import { SimHistoryModal } from './components/SimHistoryModal';
import { SimInputTitleModal } from './components/SimInputTitleModal';
import { descView } from './models/desc';

type props = {
    simulateUseCase: SimulateUseCaseInterface;
};

const CU01002Page = ({ simulateUseCase }: props) => {
    const [form] = Form.useForm();
    const { simId } = useParams();
    const [selectedHistorySimId, setSelectedHistorySimId] = React.useState<number>();
    const location = useLocation();
    const [desc, setDesc] = React.useState<any[]>([]);
    const [detailAssessment, setDetailAssessment] = React.useState<SimulateSaleProperty>();
    const [dataChart, setDataChart] = React.useState<any[]>([]);
    const [historyList, setHistoryList] = React.useState<SimHistoryInfo[]>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userSlice = useAppSelector((state) => state.user);
    const [openSimHistoryModal, setOpenSimHistoryModal] = React.useState<boolean>(false);
    const [openSimInputTitleModal, setOpenSimInputTitleModal] = React.useState<boolean>(false);
    const [btnDisabled, setBtnDisabled] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (historyList) {
            if (historyList.length >= 10) {
                setOpenSimHistoryModal(true);
            } else {
                openInputPopup();
            }
        }
    }, [historyList]);

    const getData = async (payload: SimulateEntity) => {
        try {
            dispatch(startFetching());
            const response = await simulateUseCase.getSimulateSale({
                ...payload,
                simId: payload.simId ? payload.simId : null,
            });
            scrollTop();
            const data = response.result;
            const simData = data.sim;
            setDataChart([
                {
                    id: '支出計',
                    data: [
                        {
                            x: `${data.startPoint.currentHoldingPeriod}`,
                            y: data.startPoint.currentExpense / 10000,
                        },
                        {
                            x: `${data.endPoint.currentHoldingPeriod}`,
                            y: data.endPoint.currentExpense / 10000,
                        },
                    ],
                },
                {
                    id: '収入計',
                    data: [
                        {
                            x: `${data.startPoint.currentHoldingPeriod}`,
                            y: data.startPoint.currentIncome / 10000,
                        },
                        {
                            x: `${data.endPoint.currentHoldingPeriod}`,
                            y: data.endPoint.currentIncome / 10000,
                        },
                    ],
                },
            ]);
            setDesc(
                descView([
                    {
                        center: commonHelpers.formatNumberMoney(
                            simData.currentIncomeSummary / 10000,
                            0,
                        ),
                        right: commonHelpers.formatNumberMoney(simData.breakEvenPoint),
                    },
                    {
                        center: commonHelpers.formatNumberMoney(simData.currentLoanBalance / 10000, 0),
                        right: commonHelpers.formatNumberMoney(
                            ((simData.rent - simData.managementFee) *
                                (simData.currentHoldingPeriod * 12) +
                                simData.expectedSalePrice) /
                                10000,
                            0,
                        ),
                    },
                    {
                        center: commonHelpers.formatNumberMoney(simData.currentTotalExpense / 10000, 0),
                        right: '',
                    },
                ]),
            );
            setDetailAssessment(
                {
                    loanInterestRate: simData.loanInterestRate,
                    loanPeriod: simData.loanPeriod,
                    loanPaybable: simData.loanPaybable,
                    currentHoldingPeriod: simData.currentHoldingPeriod,
                    personalFunds: simData.personalFunds,
                    rent: simData.rent,
                    managementFee: simData.managementFee,
                    expectedSalePrice: simData.expectedSalePrice,
                }
            );
            // init form value
            form.setFieldsValue({
                loanInterestRate: commonHelpers.formatNumberMoney(Number(simData.loanInterestRate)),
                loanPeriod: commonHelpers.formatNumberMoney(simData.loanPeriod),
                loanPaybable: commonHelpers.formatNumberMoney(simData.loanPaybable / 1),
                currentHoldingPeriod: commonHelpers.formatNumberMoney(simData.currentHoldingPeriod),
                personalFunds: commonHelpers.formatNumberMoney(simData.personalFunds / 1),
                rent: commonHelpers.formatNumberMoney(simData.rent),
                managementFee: commonHelpers.formatNumberMoney(simData.managementFee),
                expectedSalePrice: commonHelpers.formatNumberMoney(simData.expectedSalePrice / 1),
                title: simData.simulationTitle,
            });
            // eslint-disable-next-line no-empty
        } catch (error: any) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const replace = (value: string) => {
        if (!value) return value;
        return value.replaceAll(',', '');
    };

    const scrollTop = () => {
        document.getElementById('top-page')?.scrollIntoView({ behavior: 'smooth' });
    };

    const onSubmit = async (values: any) => {
        const payload: SimulateEntity = {
            loanInterestRate: Number(replace(values?.loanInterestRate)),
            loanPeriod: Number(replace(values?.loanPeriod)),
            loanPaybable: Number(replace(values?.loanPaybable)) * 1,
            currentHoldingPeriod: Number(replace(values?.currentHoldingPeriod)),
            personalFunds: Number(replace(values?.personalFunds)) * 1,
            rent: Number(replace(values?.rent)),
            managementFee: Number(replace(values?.managementFee)),
            expectedSalePrice: Number(replace(values?.expectedSalePrice)) * 1,
        };
        getData(payload);
    };

    const checkLogin = async () => {
        if (userSlice?.isSignedIn) {
            if (simId) {
                saveData(form.getFieldValue('title'), Number(simId));
                return;
            } else {
                openHistoryPopup();
            }
        } else {
            const confirmProps = {
                message: '以降のステップには\nログインが必要です。',
                okText: 'ログイン・アカウント作成へ',
                cancelText: 'いいえ',
                type: 'info',
                onOk: goToLoginPage,
            };
            dispatch(showConfirm(confirmProps));
        }
    };

    const goToLoginPage = () => {
        navigate(`${AUTH_ROUTES.PATH}/${AUTH_ROUTES.SUB.CU_001_00}`);
    };

    const openHistoryPopup = async () => {
        try {
            dispatch(startFetching());
            const values = form.getFieldsValue();
            const payload = {
                loanInterestRate: Number(replace(values?.loanInterestRate)),
                loanPeriod: Number(replace(values?.loanPeriod)),
                loanPaybable: Number(replace(values?.loanPaybable)) * 1,
                currentHoldingPeriod: Number(replace(values?.currentHoldingPeriod)),
                personalFunds: Number(replace(values?.personalFunds)) * 1,
                rent: Number(replace(values?.rent)),
                managementFee: Number(replace(values?.managementFee)),
                expectedSalePrice: Number(replace(values?.expectedSalePrice)) * 1,
                simId: selectedHistorySimId ?? null,
            };
            const responses = await Promise.all([
                simulateUseCase.getSimModalList('CU-010-02'),
                getData(payload),
            ]);
            const response = responses[0];
            if (response.result) {
                setHistoryList(response.result.listSim);
            }
            // eslint-disable-next-line no-empty
        } catch (err) {
        } finally {
            dispatch(completeFetching());
        }
    };

    const openInputPopup = async (simId?: number) => {
        setSelectedHistorySimId(simId);
        setHistoryList(undefined);
        setOpenSimHistoryModal(false);
        setOpenSimInputTitleModal(true);
    };

    const saveData = async (title: string, simId?: number) => {
        const values = form.getFieldsValue();
        setOpenSimInputTitleModal(false);
        const payload = {
            loanInterestRate: Number(replace(values?.loanInterestRate)),
            loanPeriod: Number(replace(values?.loanPeriod)),
            loanPaybable: Number(replace(values?.loanPaybable)) * 1,
            currentHoldingPeriod: Number(replace(values?.currentHoldingPeriod)),
            personalFunds: Number(replace(values?.personalFunds)) * 1,
            rent: Number(replace(values?.rent)),
            managementFee: Number(replace(values?.managementFee)),
            expectedSalePrice: Number(replace(values?.expectedSalePrice)) * 1,
            simId: simId ?? selectedHistorySimId ?? null,
            simulationTitle: title,
        };
        try {
            dispatch(startFetching());
            await simulateUseCase.createSimulateSale(payload);
            setBtnDisabled(true);
            openSuccessNoti({ description: getMessage('S338') });
            // eslint-disable-next-line no-empty
        } catch (error: any) {
        } finally {
            dispatch(completeFetching());
        }
    };

    React.useEffect(() => {
        const payload = {
            ...location.state,
            simId: simId,
        };
        getData(payload);
    }, []);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
            }}
            className="CU-010-02-container"
        >
            <PageTitle title={SCREEN_NAME['CU-010-02']} />
            <section
                style={{
                    padding: '10px 0px',
                    height: 'fit-content',
                    width: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'hidden',
                    marginBottom: 60,
                }}
            >
                <Content
                    detailAssessment={detailAssessment}
                    dataChart={dataChart}
                    form={form}
                    desc={desc}
                    onSubmit={onSubmit}
                    createClick={checkLogin}
                    btnDisable={btnDisabled}
                    setBtnDisable={setBtnDisabled}
                />
            </section>
            <SimHistoryModal
                data={historyList}
                open={openSimHistoryModal}
                onOk={openInputPopup}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimHistoryModal(false);
                }}
            />
            <SimInputTitleModal
                data={historyList}
                open={openSimInputTitleModal}
                onOk={saveData}
                onCancel={() => {
                    setHistoryList(undefined);
                    setOpenSimInputTitleModal(false);
                }}
            />
        </div>
    );
};

export default CU01002Page;
